import type { PhotoRecord } from '~/models/photo'
import { validatePhoto } from '~/models/photo'
import type { CalendarDate } from '~/types/dates'
import { unwrapDate } from '~/types/dates'
import type { PhotoId } from '~/types/ids'
import type { Result } from '~/utils/result'
import { err, ok } from '~/utils/result'
import type { SolaDatabase } from '../db'
import { type StorageError, storageError, toStorageError } from '../errors'
import { notifyWritten } from '../invalidation'
import { STORE } from '../schema'
import { withTransaction } from '../transactions'

/**
 * Photo metadata (D-081).
 *
 * **Every read here returns records, never bytes.** The Photos grid lists a
 * whole library from this store without touching `blobs`, which is what keeps
 * its memory cost flat as the library grows — the failure mode Checkpoint 3
 * exists to avoid on iOS.
 */

export async function putPhoto(
  db: SolaDatabase,
  record: PhotoRecord,
): Promise<Result<PhotoRecord, StorageError>> {
  const issues = validatePhoto(record)
  if (issues.length > 0) {
    return err(
      storageError('invalidRecord', issues.map((entry) => entry.message).join(' '), issues),
    )
  }

  try {
    await db.put(STORE.photos, record as never)
    notifyWritten(STORE.photos)
    return ok(record)
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

/** Every photo, oldest first — the `capturedDate` index gives ascending order. */
export async function listPhotos(
  db: SolaDatabase,
): Promise<Result<readonly PhotoRecord[], StorageError>> {
  try {
    const rows = await db.getAllFromIndex(STORE.photos, 'capturedDate')
    return ok(rows as unknown as readonly PhotoRecord[])
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

export async function getPhoto(
  db: SolaDatabase,
  id: PhotoId,
): Promise<Result<PhotoRecord | undefined, StorageError>> {
  try {
    return ok((await db.get(STORE.photos, id)) as PhotoRecord | undefined)
  } catch (cause) {
    return err(toStorageError(cause))
  }
}

/** Photos captured on or before a date, oldest first. */
export async function listPhotosOnOrBefore(
  db: SolaDatabase,
  onOrBefore: CalendarDate,
): Promise<Result<readonly PhotoRecord[], StorageError>> {
  const all = await listPhotos(db)
  if (!all.ok) return all

  const limit = unwrapDate(onOrBefore)
  return ok(all.value.filter((photo) => unwrapDate(photo.capturedDate) <= limit))
}

/**
 * Remove one photo and both of its blobs, in a single transaction.
 *
 * **Atomic deliberately, and the failure it prevents is the quiet one.** Three
 * sequential deletes that fail partway leave either an orphaned blob taking up
 * space no screen accounts for, or — far worse — a photo record pointing at
 * bytes that are gone, which renders as a broken image in a grid the user
 * believes is their history. One transaction means the photo is either entirely
 * present or entirely absent.
 *
 * **The thumbnail is deleted alongside the image, never left behind.** It is
 * derived data, so an orphan is invisible until a storage warning appears for
 * space nothing can explain.
 *
 * Deleting an id that is not present is not an error, matching every other
 * delete path in this codebase: two taps on the same row should not produce a
 * failure message about data that is already gone.
 */
export async function deletePhoto(
  db: SolaDatabase,
  id: PhotoId,
): Promise<Result<void, StorageError>> {
  const existing = await getPhoto(db, id)
  if (!existing.ok) return existing
  if (existing.value === undefined) return ok(undefined)

  const { blobId, thumbnailBlobId } = existing.value

  const result = await withTransaction(db, [STORE.photos, STORE.blobs], async (tx) => {
    await tx.objectStore(STORE.photos).delete(id)
    await tx.objectStore(STORE.blobs).delete(blobId)
    await tx.objectStore(STORE.blobs).delete(thumbnailBlobId)
  })
  if (!result.ok) return result

  notifyWritten(STORE.photos)
  notifyWritten(STORE.blobs)
  return ok(undefined)
}

/**
 * Remove every photo and every blob they own.
 *
 * This is `privacy-and-storage.md`'s "delete all photos without deleting other
 * data". It is offered from Settings at Checkpoint 4; the repository path is
 * here because it belongs with the record it deletes and because a partial
 * version of it written later would be likely to forget the thumbnails.
 *
 * Returns the number of photos removed so the confirmation can state a real
 * count rather than a guess.
 */
export async function deleteAllPhotos(db: SolaDatabase): Promise<Result<number, StorageError>> {
  const all = await listPhotos(db)
  if (!all.ok) return all
  if (all.value.length === 0) return ok(0)

  const result = await withTransaction(db, [STORE.photos, STORE.blobs], async (tx) => {
    for (const photo of all.value) {
      await tx.objectStore(STORE.photos).delete(photo.id)
      await tx.objectStore(STORE.blobs).delete(photo.blobId)
      await tx.objectStore(STORE.blobs).delete(photo.thumbnailBlobId)
    }
  })
  if (!result.ok) return result

  notifyWritten(STORE.photos)
  notifyWritten(STORE.blobs)
  return ok(all.value.length)
}
